import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buildMergeToken } from "@/lib/utils/merge-vars";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variableKey: string;        // e.g. "first_name"
  variableLabel: string;      // e.g. "First name"
  defaultFallback?: string;   // pre-filled fallback (per-field)
  onInsert: (token: string) => void;
}

export default function InsertVariableDialog({
  open, onOpenChange, variableKey, variableLabel, defaultFallback = "", onInsert,
}: Props) {
  const { t } = useTranslation();
  const [fallback, setFallback] = useState(defaultFallback);

  useEffect(() => {
    if (open) setFallback(defaultFallback);
  }, [open, defaultFallback]);

  const insert = (withFallback: boolean) => {
    onInsert(buildMergeToken(variableKey, withFallback ? fallback : ""));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("forms.variable.insertTitle", { label: variableLabel.toLowerCase() })}</DialogTitle>
          <DialogDescription>
            {t("forms.variable.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="merge-fallback" className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("forms.variable.fallbackLabel")}
          </Label>
          <Input
            id="merge-fallback"
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            placeholder={defaultFallback || t("forms.variable.fallbackPlaceholder")}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                insert(true);
              }
            }}
          />
          <p className="font-mono text-xs text-muted-foreground">
            {t("forms.variable.willInsert", { token: buildMergeToken(variableKey, fallback) })}
          </p>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => insert(false)}>
            {t("forms.variable.insertWithoutFallback")}
          </Button>
          <Button onClick={() => insert(true)}>{t("forms.variable.insert")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
